import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { siteEditor, files as filesApi } from '../services/api';
import { ArrowRight } from 'lucide-react';

// YouTube URL → embed src
function youtubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?rel=0` : null;
}

// Normalize a stored file/URL to a fully-qualified src.
function resolveUrl(u) {
  if (!u) return '';
  return filesApi.urlFor(u);
}

// Renders a href — supports mailto:, tel:, external http(s), uploaded files, internal routes.
function LinkOrAnchor({ href, target, children, testId, className }) {
  const raw = (href || '').trim();
  if (!raw) return <span className={className} data-testid={testId}>{children}</span>;
  const isMail = raw.startsWith('mailto:');
  const isTel = raw.startsWith('tel:');
  const isHttp = /^https?:\/\//i.test(raw);
  const isFile = raw.startsWith('/api/');
  const isAnchor = raw.startsWith('#');
  if (isMail || isTel || isHttp || isFile) {
    return (
      <a href={isFile ? resolveUrl(raw) : raw} target={target || (isHttp ? '_blank' : '_self')}
         rel={target === '_blank' || isHttp ? 'noopener noreferrer' : undefined}
         className={className} data-testid={testId}>{children}</a>
    );
  }
  if (isAnchor) {
    return <a href={raw} className={className} data-testid={testId}>{children}</a>;
  }
  return <Link to={raw} className={className} data-testid={testId}>{children}</Link>;
}

function BtnClasses(style) {
  const base = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-colors';
  if (style === 'secondary') return `${base} bg-neutral-900 hover:bg-neutral-800 text-white`;
  if (style === 'ghost') return `${base} border border-neutral-300 hover:border-green-500 text-neutral-800 hover:text-green-700 bg-white`;
  return `${base} bg-green-600 hover:bg-green-700 text-white`;
}

function SectionRenderer({ section }) {
  const c = section.content || {};
  const type = section.type;
  if (type === 'heading') {
    const level = c.level || 'h2';
    const align = c.align || 'left';
    const cls = `font-black tracking-tight ${
      level === 'h1' ? 'text-4xl sm:text-5xl lg:text-6xl' :
      level === 'h3' ? 'text-xl md:text-2xl' :
      'text-2xl md:text-3xl'
    } text-${align}`;
    const Tag = level;
    return <Tag className={cls} data-testid={`site-section-${section.id}`}>{c.text || ''}</Tag>;
  }
  if (type === 'richtext') {
    const align = c.align || 'left';
    return (
      <div className={`prose max-w-none text-${align} text-neutral-700 whitespace-pre-wrap leading-relaxed`}
           data-testid={`site-section-${section.id}`}>
        {c.text || ''}
      </div>
    );
  }
  if (type === 'image') {
    const src = resolveUrl(c.url);
    if (!src) return null;
    const align = c.align || 'center';
    return (
      <figure className={`text-${align}`} data-testid={`site-section-${section.id}`}>
        <img src={src} alt={c.alt || ''} className="rounded-2xl shadow-lg max-w-full h-auto inline-block" />
        {c.caption && <figcaption className="mt-2 text-sm text-neutral-500">{c.caption}</figcaption>}
      </figure>
    );
  }
  if (type === 'gallery') {
    const imgs = Array.isArray(c.images) ? c.images.filter(x => x && x.url) : [];
    if (!imgs.length) return null;
    const align = c.align || 'center';
    const justifyCls = align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';
    return (
      <div className={`flex ${justifyCls}`} data-testid={`site-section-${section.id}`}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full">
          {imgs.map((img, i) => (
            <figure key={i} className="rounded-xl overflow-hidden bg-neutral-100 border border-neutral-200">
              <img src={resolveUrl(img.url)} alt={img.alt || ''} className="w-full h-48 object-cover" />
              {img.caption && <figcaption className="p-2 text-xs text-neutral-500">{img.caption}</figcaption>}
            </figure>
          ))}
        </div>
      </div>
    );
  }
  if (type === 'banner') {
    const bg = resolveUrl(c.image);
    const align = c.align || 'center';
    const textAlignCls = `text-${align}`;
    return (
      <div className="relative rounded-3xl overflow-hidden shadow-2xl min-h-[280px] flex items-center justify-center"
           data-testid={`site-section-${section.id}`}
           style={bg ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${bg})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: 'linear-gradient(135deg, #065f46, #14532d)' }}>
        <div className={`${textAlignCls} px-6 py-12 text-white max-w-3xl w-full`}>
          {c.headline && <h2 className="text-3xl md:text-5xl font-black">{c.headline}</h2>}
          {c.subtext && <p className="mt-3 text-base md:text-lg text-white/90">{c.subtext}</p>}
          {c.buttonLabel && c.buttonHref && (
            <div className={`mt-6 flex ${align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center'}`}>
              <LinkOrAnchor href={c.buttonHref} target={c.buttonTarget}
                className={BtnClasses('primary') + ' shadow-xl'}
                testId={`site-banner-cta-${section.id}`}>
                {c.buttonLabel} <ArrowRight size={16} />
              </LinkOrAnchor>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (type === 'buttons') {
    const btns = Array.isArray(c.buttons) ? c.buttons.filter(b => b && b.label) : [];
    if (!btns.length) return null;
    const align = c.align || 'center';
    return (
      <div className={`flex flex-wrap gap-3 justify-${align === 'left' ? 'start' : align === 'right' ? 'end' : 'center'}`}
           data-testid={`site-section-${section.id}`}>
        {btns.map((b, i) => (
          <LinkOrAnchor key={i} href={b.href} target={b.target}
            className={BtnClasses(b.style)}
            testId={`site-button-${section.id}-${i}`}>
            {b.label}
          </LinkOrAnchor>
        ))}
      </div>
    );
  }
  if (type === 'video') {
    const src = youtubeEmbed(c.url);
    if (!src) return null;
    const align = c.align || 'center';
    const justifyCls = align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';
    return (
      <div className={`flex ${justifyCls}`} data-testid={`site-section-${section.id}`}>
        <div className="rounded-2xl overflow-hidden shadow-xl border border-neutral-200 aspect-video bg-black w-full max-w-3xl">
          <iframe title="Embedded video" src={src} width="100%" height="100%"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            allowFullScreen frameBorder="0" className="w-full h-full" />
        </div>
      </div>
    );
  }
  if (type === 'divider') {
    return <hr className="border-neutral-200" data-testid={`site-section-${section.id}`} />;
  }
  return null;
}

/**
 * Renders published sections for a public page. Nothing displays until an
 * admin creates + publishes at least one section for this pageKey.
 */
export default function SiteSections({ pageKey, className = '' }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    siteEditor.publicSections(pageKey)
      .then((r) => { if (live) { setRows(r || []); setLoaded(true); } })
      .catch(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [pageKey]);

  if (!loaded || rows.length === 0) return null;
  // Group by vertical position; within each band preserve backend order.
  const V_ORDER = ['top', 'middle', 'bottom'];
  const groups = { top: [], middle: [], bottom: [] };
  for (const s of rows) {
    const vp = ((s.content || {}).vPos) || 'middle';
    (groups[vp] || groups.middle).push(s);
  }
  const ordered = V_ORDER.flatMap(v => groups[v]);
  return (
    <section className={`max-w-5xl mx-auto px-4 py-10 space-y-8 ${className}`}
             data-testid={`site-sections-${pageKey}`}>
      {ordered.map(sec => <SectionRenderer key={sec.id} section={sec} />)}
    </section>
  );
}
